import { defineComponent as _defineComponent } from 'vue'
import Vue, {
  computed,
  onMounted,
  provide,
  ref,
  shallowRef,
  useSlots,
  VNode,
} from 'vue';
import SlotComponent from './SlotComponent.vue';
import { isEmpty, isNil, isVueComponent } from '@/utils/misc';
import NumberedBreadcrumbItem from './NumberedBreadcrumbItem.vue';
import { type WizardStep } from './WizardStep.vue';
import { TranslateResult } from 'vue-i18n';
import { useI18n } from '@/composables/i18n';


/** @deprecated THIS COMPONENT IS FROZEN **/
const __default__ = {};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  __name: 'Wizard',
  props: {
    title: null,
    onSubmit: { type: Function, default: () => async () => {} }
  },
  emits: ["close"],
  setup(__props: any, { emit }: { emit: ((e: 'close') => void), expose: any, slots: any, attrs: any }) {

const props = __props as { title: string | TranslateResult, onSubmit: () => Promise<void> };





const $slots = useSlots().default?.();
provide('refreshSteps', refreshSteps);

const currentStep = ref(0);
const isLeaving = ref(false);
const isSubmitting = ref(false);
const steps = shallowRef<Array<WizardStep>>([]);

const { t } = useI18n();

onMounted(() => {
  refreshSteps();
});

function isWizardStep($vnode: VNode | undefined): boolean {
  return ($vnode?.componentInstance as any | undefined)?.isWizardStep === true;
}

function getDefaultSlotsRecursively(component: Vue | undefined): Vue[] {
  if (!component) {
    return [];
  }
  const slotsAndChildren: VNode[] = [
    ...(component.$slots.default ?? []),
    ...(component.$children as Vue[])
      .filter((x) => isVueComponent(x))
      .map((x) => x.$vnode),
  ].filter((x) => !isNil(x.componentInstance));

  if (!isEmpty(slotsAndChildren)) {
    const wizardSteps = slotsAndChildren.filter((x) => isWizardStep(x));
    const nonWizardSteps = slotsAndChildren.filter((x) => !isWizardStep(x));

    return [
      component,
      ...wizardSteps.map((x) => x.componentInstance!),
      ...nonWizardSteps.flatMap((x) =>
        getDefaultSlotsRecursively(x.componentInstance),
      ),
    ];
  }

  return [component];
}

function getWizardSteps() {
  const slots = ($slots ?? []).flatMap((x) =>
    getDefaultSlotsRecursively(x.componentInstance),
  );
  const wizardSteps = slots.filter((x) => isWizardStep(x.$vnode));
  return wizardSteps.map(
    (slot) => slot.$vnode.componentInstance! as WizardStep,
  );
}

function refreshSteps() {
  steps.value = getWizardSteps();
  if (!isEmpty(steps.value)) {
    steps.value[0].enter(true);
  }
}

function goToPreviousStep() {
  if (isFirstStep.value) {
    return;
  }

  nonHiddenSteps.value[currentStep.value].leave(true);
  currentStep.value--;
  nonHiddenSteps.value[currentStep.value].enter(false);
}

async function goToNextStep() {
  if (isLastStep.value) {
    return;
  }

  const beforeLeaveResult =
    nonHiddenSteps.value[currentStep.value].beforeLeave();
  let canLeave = false;
  if (beforeLeaveResult instanceof Promise) {
    isLeaving.value = true;
    canLeave = await beforeLeaveResult;
    isLeaving.value = false;
  } else {
    canLeave = beforeLeaveResult;
  }

  if (!canLeave) {
    return;
  }

  nonHiddenSteps.value[currentStep.value].leave(false);
  currentStep.value++;
  nonHiddenSteps.value[currentStep.value].enter(true);
}

async function submit() {
  isSubmitting.value = true;
  await props.onSubmit().finally(() => (isSubmitting.value = false));
}

function getSlotComponentInstance(index: number) {
  return steps.value[index];
}

const nonHiddenSteps = computed<WizardStep[]>(() => {
  return steps.value.filter((slot) => slot.hidden === false);
});

const isFirstStep = computed(() => currentStep.value === 0);

const isLastStep = computed(
  () => currentStep.value === nonHiddenSteps.value.length - 1,
);

const isNextButtonEnabled = computed(() => {
  if (isEmpty(steps.value)) {
    return false;
  }

  return nonHiddenSteps.value[currentStep.value].allowNextIf;
});

return { __sfc: true,props, emit, $slots, currentStep, isLeaving, isSubmitting, steps, t, isWizardStep, getDefaultSlotsRecursively, getWizardSteps, refreshSteps, goToPreviousStep, goToNextStep, submit, getSlotComponentInstance, nonHiddenSteps, isFirstStep, isLastStep, isNextButtonEnabled, SlotComponent, NumberedBreadcrumbItem }
}

})